import axios from 'axios';

// API base URL from environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
export const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if it's NOT the profile endpoint (which is handled by AuthContext)
      const isProfileEndpoint = error.config?.url?.includes('/auth/profile');
      
      if (!isProfileEndpoint) {
        // Unauthorized - clear token and redirect to login
        console.log('❌ 401 Unauthorized - Redirigiendo a login');
        localStorage.removeItem('token');
        
        // Use setTimeout to avoid redirect loops
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// API service functions
export const authService = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
    
  register: (userData: {
    email: string;
    password: string;
    full_name: string;
    phone?: string;
    location?: string;
    linkedin_url?: string;
  }) => api.post('/auth/register', userData),
  
  getProfile: () => api.get('/auth/profile'),
  
  updateProfile: (data: any) => api.put('/auth/profile', data),
  
  logout: () => api.post('/auth/logout'),
};

export const resumeService = {
  generate: (data: {
    job_data: {
      title: string;
      company: string;
      description: string;
      location: string;
      url?: string;
      platform?: string;
    };
    options: {
      platform?: string;
      template_type?: number;
      language?: string;
      include_cover_letter?: boolean;
    };
  }) => api.post('/resumes/generate', data),
  
  getHistory: (limit?: number) => api.get(`/resumes/history?limit=${limit || 20}`),
  
  getTemplates: () => api.get('/resumes/templates'),
  
  getPlatforms: () => api.get('/resumes/platforms'),
  
  checkEligibility: (data: {
    job_description: string;
    job_location: string;
    salary_info?: string;
  }) => api.post('/resumes/check-eligibility', data),
  
  calculateMatch: (data: { job_description: string }) =>
    api.post('/resumes/calculate-match', data),
  
  getStats: () => api.get('/resumes/stats'),
  
  downloadFile: (fileType: string, directoryName: string, filename: string) =>
    api.get(`/resumes/download/${fileType}/${directoryName}/${filename}`, {
      responseType: 'blob',
    }),
};

export const jobService = {
  // Job Search
  searchJobs: (params: {
    query?: string;
    location?: string;
    platform?: string;
    limit?: number;
    remote?: boolean;
    job_type?: string;
    experience_level?: string;
    salary_min?: number;
  }) => {
    // If platform is 'lever', use cached results from DB (2,255+ jobs)
    // Otherwise, use live search API
    if (params.platform === 'lever') {
      return api.get('/jobs/search/results', { params });
    }
    return api.get('/jobs/search', { params });
  },
  
  getCachedResults: (params?: {
    query?: string;
    location?: string;
    platform?: string;
    remote?: boolean;
    limit?: number;
    offset?: number;
  }) => api.get('/jobs/search/results', { params }),
  
  // Job Applications
  getJobs: (params?: { status?: string; limit?: number }) => 
    api.get('/jobs', { params }),
  
  createJob: (data: {
    title: string;
    company: string;
    location?: string;
    description?: string;
    job_url: string;
    platform?: string;
  }) => api.post('/jobs', data),
  
  getJob: (id: number) => api.get(`/jobs/${id}`),
  
  updateJobStatus: (id: number, status: string) => 
    api.put(`/jobs/${id}/status`, { status }),
  
  // Auto-Apply (Playwright automation)
  autoApplyToJob: (jobId: string) => 
    api.post(`/jobs/${jobId}/auto-apply`),
  
  // Auto-Apply directo (sin requerir job en DB)
  autoApplyDirect: (data: {
    job_url: string;
    job_title: string;
    company: string;
    location?: string;
  }) => api.post(`/jobs/auto-apply`, data),

  // 🤖 NEW: AI-powered auto-apply using Browser-Use
  autoApplyAI: (data: {
    job_url: string;
    job_title: string;
    company: string;
    location?: string;
  }) => api.post(`/jobs/auto-apply-ai`, data),
};

export const platformService = {
  getPlatforms: () => api.get('/platforms'),
  getPlatform: (id: string) => api.get(`/platforms/${id}`),
  getSearchPlatforms: () => api.get('/platforms/search/supported'),
};

export const applicationService = {
  // Apply to jobs
  applyToJob: (formData: FormData) => 
    api.post('/applications/apply/lever', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  // Get user's applications (las que realmente aplicaste con auto-apply)
  getApplications: (params?: { status?: string; limit?: number }) => 
    api.get('/jobs/applications', { params }),
  
  // Update application status
  updateApplicationStatus: (id: number, status: string) => 
    api.patch(`/jobs/applications/${id}/status`, { status }),
  
  // Get application statistics
  getStats: () => api.get('/jobs/applications/stats'),
};

export const subscriptionService = {
  getPlans: () => api.get('/subscriptions'),
  subscribe: (planId: string) => api.post('/subscriptions/subscribe', { plan_id: planId }),
  cancel: () => api.post('/subscriptions/cancel'),
};